import type { KeyboardEvent } from "react";
import { canv } from "@/canv/global";
import { escapeString } from "@/canv/utils";
import { getFirstSelected } from "@/canvasController/helpers";
import type { TextWidgetV2 } from "@/elements/text/textV2";
import { eventEmitter } from "@/eventEmitter";

export class LinkInputController {
    isInputVisible = false;
    textWidget: TextWidgetV2 | null = null;
    extraUpdate: (() => void) | null = null;

    show(currentLink: string, textWidget: TextWidgetV2, extraUpdate: (() => void) | null = null) {
        this.hide();

        this.textWidget = textWidget;
        this.extraUpdate = extraUpdate;

        eventEmitter.dispatch("link.input.defaultValue", { value: currentLink });

        this.locate();
    }

    hide() {
        eventEmitter.dispatch("link.input.coordinates", {
            coordinates: null,
        });

        this.isInputVisible = false;

        this.textWidget = null;
        this.extraUpdate = null;
    }

    locate() {
        const selectedProps = canv.canvasController.getSelectedProps();

        const startX = selectedProps.startX;
        const startY = selectedProps.startY;
        const width = selectedProps.width;

        let xTarget = (startX + width / 2) * canv.m[0];
        let yTarget = startY * canv.m[3];

        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        if (cursorTool?.isMode("interaction")) {
            const interactionLocale = getFirstSelected().locateWidgetMenu();

            if (interactionLocale?.status) {
                xTarget = interactionLocale.x * canv.m[3];
                yTarget = interactionLocale.y * canv.m[3];
            }
        }

        eventEmitter.dispatch("link.input.coordinates", {
            coordinates: { top: yTarget, left: xTarget },
        });

        this.isInputVisible = true;
    }

    prepareLink(rawText: string) {
        let link = rawText;

        if (
            rawText !== "" &&
            !link.startsWith("https://") &&
            !link.startsWith("http://") &&
            !link.startsWith("file://")
        ) {
            link = `https://${link}`;
        }

        return escapeString(link);
    }

    handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (!this.textWidget) {
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();

            const input = event.currentTarget;
            const link = input.value;

            this.textWidget.menuLinkSet(this.prepareLink(link));
            if (this.extraUpdate != null) {
                // linkSet - это внутренняя механика textWidget, которая меняет его dimensions. Например, как при paste. Но если сигнал о paste передается через TryAction
                // и виджет (parent-widget), частью которого является этот textWidget, просто вызывает updateDims после TryAction, то после вызова menuLinkSet parent-widget
                // ничего об это не узнает. Поэтому делаем callback
                this.extraUpdate();
            }
            this.hide();

            if (!canv.textEditingController.getIsEditing()) {
                canv.appInterface.inputFocused = 2;
            }
        }
    };

    handleClickOutside(wrapper: HTMLDivElement | null) {
        return (event: MouseEvent) => {
            event.preventDefault();
            event.stopPropagation();

            if (!(event.target instanceof Node)) {
                return;
            }

            if (wrapper?.contains(event.target)) {
                return;
            }

            this.hide();
            canv.textEditingController.hideSelectionRects();
            canv.widgetMenu.reopen();
            canv.redraw();
        };
    }
}
